import {
  AssetPreparationStatusEnum,
  EducationalQualificationEnum,
  InvestmentCancellationEnum,
  Person,
  ProductTypeEnum,
  RoleEnum,
  StepEnum,
  SurveyWaiverTypeEnum,
  TokenizationApiOperationStatus,
  TokenTransferStatusEnum,
  WalletTypeEnum,
} from 'api';
import { PersonToLegalPerson, PersonToNaturalPerson } from 'helper/cast';
import { EducationalQualification, Occupation, PersonType } from 'ui/types/person';
import { TransferStatus } from 'ui/types/transfer';
import { ProductType } from 'ui/types/asset';
import { assertUnreachable } from 'ui/helper/unreachable';
import { find } from 'lodash';
import { WalletType } from 'ui/types/wallet';
import { SurveyWaiverType } from 'ui/types/survey-waiver';
import { Step } from 'ui/types/step';
import { InvestmentCancellation } from 'ui/types/investment-cancellation';
import { AssetPreparationStatus } from 'ui/types/asset-preparation';
import { TokenizationOperationStatus } from 'ui/types/tokenization-api';
import { PlatformUrl } from 'ui/types/urls';

export const convertEducationalQualificationFromApi = (x: EducationalQualificationEnum): EducationalQualification => {
  switch (x) {
    case EducationalQualificationEnum.HIGH_SCHOOL_DIPLOMA___A_LEVELS:
      return EducationalQualification.HighSchoolDiplomaALevels;
    case EducationalQualificationEnum.SECONDARY_SCHOOL_DIPLOMA:
      return EducationalQualification.SecondarySchoolDiploma;
    case EducationalQualificationEnum.BACHELOR_AND_OR_MASTER_DEGREE:
      return EducationalQualification.BachelorAndOrMasterDegree;
    case EducationalQualificationEnum.DOCTORAL_DEGREE:
      return EducationalQualification.DoctoralDegree;
    case EducationalQualificationEnum.NOT_SPECIFIED:
      return EducationalQualification.NotSpecified;
  }

  return assertUnreachable(x);
};

export const convertOccupationFromApi = (x: string): Occupation => {
  switch (x) {
    case Occupation.NotSpecified:
      return Occupation.NotSpecified;
    case Occupation.Employee:
      return Occupation.Employee;
    case Occupation.Apprentice:
      return Occupation.Apprentice;
    case Occupation.Pupil:
      return Occupation.Pupil;
    case Occupation.Student:
      return Occupation.Student;
    case Occupation.SelfEmployed:
      return Occupation.SelfEmployed;
    case Occupation.Retiree:
      return Occupation.Retiree;
    case Occupation.MarginalEmploymentMiniJob:
      return Occupation.MarginalEmploymentMiniJob;
    case Occupation.WithoutEmployment:
      return Occupation.WithoutEmployment;
  }

  return Occupation.NotSpecified;
};

export const convertEducationalQualificationToApi = (x: EducationalQualification): EducationalQualificationEnum => {
  switch (x) {
    case EducationalQualification.HighSchoolDiplomaALevels:
      return EducationalQualificationEnum.HIGH_SCHOOL_DIPLOMA___A_LEVELS;
    case EducationalQualification.SecondarySchoolDiploma:
      return EducationalQualificationEnum.SECONDARY_SCHOOL_DIPLOMA;
    case EducationalQualification.BachelorAndOrMasterDegree:
      return EducationalQualificationEnum.BACHELOR_AND_OR_MASTER_DEGREE;
    case EducationalQualification.DoctoralDegree:
      return EducationalQualificationEnum.DOCTORAL_DEGREE;
    case EducationalQualification.NotSpecified:
      return EducationalQualificationEnum.NOT_SPECIFIED;
  }

  return assertUnreachable(x);
};

export const extractNaturalAndLegalFromPerson = (person?: Person) => {
  if (!person)
    return {
      personType: undefined,
      naturalPerson: undefined,
      legalPerson: undefined,
    };

  const legalPerson = PersonToLegalPerson(person);

  return {
    personType: legalPerson ? PersonType.Legal : PersonType.Natural,
    naturalPerson:
      PersonToNaturalPerson(person) || PersonToNaturalPerson(find(person.persons as any, { role: RoleEnum.CEO })),
    legalPerson,
  };
};

export function convertProductTypeFromApi(x: ProductTypeEnum): ProductType {
  switch (x) {
    case ProductTypeEnum.AIF:
      return ProductType.AIF;
    case ProductTypeEnum.BOND:
      return ProductType.BOND;
    case ProductTypeEnum.DEBT_SECURITY:
      return ProductType.DEBTSECURITY;
    case ProductTypeEnum.DIGITAL_COMPANY_SHARE:
      return ProductType.DIGITAL_COMPANY_SHARE;
    case ProductTypeEnum.PARTICIPATION_PAPER:
      return ProductType.PARTICIPATION_PAPER;
    case ProductTypeEnum.PARTICIPATION_RIGHT:
      return ProductType.PARTICIPATION_RIGHT;
    case ProductTypeEnum.SUBORDINATED_LOAN:
      return ProductType.SUBORDINATED_LOAN;
    case ProductTypeEnum.TOKENIZED_LIMITED_PARTNER_SHARES:
      return ProductType.TOKENIZEDLIMITEDPARTNERSHARES;
    case ProductTypeEnum.TOKENIZED_ARTWORK:
      return ProductType.TOKENIZEDARTWORK;
    case ProductTypeEnum.CONVERTIBLE_BOND:
      return ProductType.CONVERTIBLEBOND;
    case ProductTypeEnum.FUND_SHARE:
      return ProductType.FUNDSHARE;
    case ProductTypeEnum.REGISTERED_BOND:
      return ProductType.REGISTERED_BOND;
  }

  return assertUnreachable(x);
}

export function convertTransferStatusFromApi(x: TokenTransferStatusEnum): TransferStatus {
  switch (x) {
    case TokenTransferStatusEnum.PENDING:
      return TransferStatus.PENDING;
    case TokenTransferStatusEnum.TFA_REQUESTED:
      return TransferStatus.TFA_REQUESTED;
    case TokenTransferStatusEnum.TFA_CONFIRMED:
      return TransferStatus.TFA_CONFIRMED;
    case TokenTransferStatusEnum.PROCESSING:
      return TransferStatus.PROCESSING;

    case TokenTransferStatusEnum.TFA_REJECTED:
      return TransferStatus.TFA_REJECTED;
    case TokenTransferStatusEnum.APPROVER_REJECTED:
      return TransferStatus.APPROVER_REJECTED;
    case TokenTransferStatusEnum.COMPLETED:
      return TransferStatus.COMPLETED;
    case TokenTransferStatusEnum.FAILED:
      return TransferStatus.FAILED;
  }

  return assertUnreachable(x);
}

export const convertWalletTypeFromApi = (x: WalletTypeEnum): WalletType => {
  switch (x) {
    case WalletTypeEnum.TANGANY:
      return WalletType.TANGANY;
    case WalletTypeEnum.MOBILE:
      return WalletType.MOBILE;
    case WalletTypeEnum.GENERIC:
      return WalletType.GENERIC;
    case WalletTypeEnum.CASHLINK:
      return WalletType.CASHLINK;
  }

  return assertUnreachable(x);
};

export const convertWalletTypeToApi = (x: WalletType): WalletTypeEnum => {
  switch (x) {
    case WalletType.TANGANY:
      return WalletTypeEnum.TANGANY;
    case WalletType.MOBILE:
      return WalletTypeEnum.MOBILE;
    case WalletType.GENERIC:
      return WalletTypeEnum.GENERIC;
    case WalletType.CASHLINK:
    case WalletType.CASHLINKETHEREUM:
    case WalletType.CASHLINKSTELLAR:
      return WalletTypeEnum.CASHLINK;
  }

  return assertUnreachable(x);
};

export const convertSurveyWaiverTypeFromApi = (x: SurveyWaiverTypeEnum): SurveyWaiverType => {
  switch (x) {
    case SurveyWaiverTypeEnum.NO_ANSWERS:
      return SurveyWaiverType.NOANSWERS;
    case SurveyWaiverTypeEnum.UNSUITABLE:
      return SurveyWaiverType.UNSUITABLE;
  }

  return assertUnreachable(x);
};

export const convertInvestmentCancellationTypeFromApi = (x: InvestmentCancellationEnum): InvestmentCancellation => {
  switch (x) {
    case InvestmentCancellationEnum.KYC_INSUFFICIENT:
      return InvestmentCancellation.KYCINSUFFICIENT;
    case InvestmentCancellationEnum.ABORTION:
      return InvestmentCancellation.ABORTION;
    case InvestmentCancellationEnum.REJECTION:
      return InvestmentCancellation.REJECTION;
    case InvestmentCancellationEnum.PAYMENT_MISSING:
      return InvestmentCancellation.PAYMENTMISSING;
    case InvestmentCancellationEnum.CANCELLATION:
      return InvestmentCancellation.CANCELLATION;
    case InvestmentCancellationEnum.REJECTION_ECSP:
      return InvestmentCancellation.REJECTION;
    case InvestmentCancellationEnum.DISTRIBUTION_INFORMATION_UPDATED:
      return InvestmentCancellation.DISTRIBUTION_INFORMATION_UPDATED;
  }

  return assertUnreachable(x);
};

export const convertInvestmentIssuanceStatusFromApi = (
  x: TokenizationApiOperationStatus,
): TokenizationOperationStatus => {
  switch (x) {
    case TokenizationApiOperationStatus.FAILED:
      return TokenizationOperationStatus.Failed;
    case TokenizationApiOperationStatus.PROCESSING:
      return TokenizationOperationStatus.Processing;
    case TokenizationApiOperationStatus.SUCCEEDED:
      return TokenizationOperationStatus.Succeeded;
    case TokenizationApiOperationStatus.UNKNOWN:
      return TokenizationOperationStatus.Unknown;
    case TokenizationApiOperationStatus.WAITING:
      return TokenizationOperationStatus.Waiting;
    case TokenizationApiOperationStatus.QUEUED:
      return TokenizationOperationStatus.Queued;
  }

  return assertUnreachable(x);
};

export const convertSurveyWaiverTypeToApi = (x: SurveyWaiverType): SurveyWaiverTypeEnum => {
  switch (x) {
    case SurveyWaiverType.NOANSWERS:
      return SurveyWaiverTypeEnum.NO_ANSWERS;
    case SurveyWaiverType.UNSUITABLE:
      return SurveyWaiverTypeEnum.UNSUITABLE;
  }

  return assertUnreachable(x);
};

export const converAssetPreparationStatusFromApi = (x: AssetPreparationStatusEnum): AssetPreparationStatus => {
  switch (x) {
    case AssetPreparationStatusEnum.STARTED:
      return AssetPreparationStatus.STARTED;
    case AssetPreparationStatusEnum.FINISHED:
      return AssetPreparationStatus.FINISHED;
    case AssetPreparationStatusEnum.REJECTED:
      return AssetPreparationStatus.REJECTED;
  }

  return assertUnreachable(x);
};

export const convertStepFromApi = (x: StepEnum): Step => {
  switch (x) {
    case StepEnum.ACCOUNT_SETUP:
      return Step.ACCOUNTSETUP;
    case StepEnum.INVESTMENT_AMOUNT:
      return Step.INVESTMENTAMOUNT;
    case StepEnum.VIB:
      return Step.VIB;
    case StepEnum.OFFER_CREATION:
      return Step.OFFERCREATION;
    case StepEnum.QUESTIONNAIRE:
      return Step.QUESTIONNAIRE;
    case StepEnum.BENEFICIARY:
      return Step.BENEFICIARY;
    case StepEnum.IDENTIFICATION:
      return Step.IDENTIFICATION;
    case StepEnum.RESUME_IDENTIFICATION:
      return Step.RESUMEIDENTIFICATION;
    case StepEnum.IDENTIFICATION_REJECTED:
      return Step.IDENTIFICATIONREJECTED;
    case StepEnum.IDENTIFICATION_FAILED:
      return Step.IDENTIFICATIONFAILED;
    case StepEnum.WAITING_KYC:
      return Step.WAITINGKYC;
    case StepEnum.WAITING_ACCEPTANCE:
      return Step.WAITINGACCEPTANCE;
    case StepEnum.WAITING_COMPLIANCE:
      return Step.WAITINGCOMPLIANCE;
    case StepEnum.PAYMENT:
      return Step.PAYMENT;
    case StepEnum.TAX_INFORMATION:
      return Step.TAXINFORMATION;
    case StepEnum.SELECT_WALLET:
      return Step.SELECTWALLET;
    case StepEnum.SETUP_MOBILE_WALLET:
      return Step.SETUPMOBILEWALLET;
    case StepEnum.PREPARE_ASSET:
      return Step.PREPAREASSET;
    case StepEnum.CONFIRM_WALLET:
      return Step.CONFIRMWALLET;
    case StepEnum.FINAL:
      return Step.FINAL;
    case StepEnum.UNCONFIRMED:
      return Step.UNCONFIRMED;
    case StepEnum.ADDITIONAL_INFORMATION:
      return Step.ADDITIONALINFORMATION;
    case StepEnum.CANCELED:
      return Step.CANCELED;
  }

  return assertUnreachable(x);
};

export const convertStepToApi = (x: Step): StepEnum => {
  switch (x) {
    case Step.ACCOUNTSETUP:
      return StepEnum.ACCOUNT_SETUP;
    case Step.INVESTMENTAMOUNT:
      return StepEnum.INVESTMENT_AMOUNT;
    case Step.VIB:
      return StepEnum.VIB;
    case Step.OFFERCREATION:
      return StepEnum.OFFER_CREATION;
    case Step.QUESTIONNAIRE:
      return StepEnum.QUESTIONNAIRE;
    case Step.BENEFICIARY:
      return StepEnum.BENEFICIARY;
    case Step.IDENTIFICATION:
      return StepEnum.IDENTIFICATION;
    case Step.RESUMEIDENTIFICATION:
      return StepEnum.RESUME_IDENTIFICATION;
    case Step.IDENTIFICATIONREJECTED:
      return StepEnum.IDENTIFICATION_REJECTED;
    case Step.IDENTIFICATIONFAILED:
      return StepEnum.IDENTIFICATION_FAILED;
    case Step.WAITINGKYC:
      return StepEnum.WAITING_KYC;
    case Step.WAITINGACCEPTANCE:
      return StepEnum.WAITING_ACCEPTANCE;
    case Step.WAITINGCOMPLIANCE:
      return StepEnum.WAITING_COMPLIANCE;
    case Step.PAYMENT:
      return StepEnum.PAYMENT;
    case Step.TAXINFORMATION:
      return StepEnum.TAX_INFORMATION;
    case Step.SELECTWALLET:
      return StepEnum.SELECT_WALLET;
    case Step.PREPAREASSET:
      return StepEnum.PREPARE_ASSET;
    case Step.SETUPMOBILEWALLET:
      return StepEnum.SETUP_MOBILE_WALLET;
    case Step.CONFIRMWALLET:
      return StepEnum.CONFIRM_WALLET;
    case Step.FINAL:
      return StepEnum.FINAL;
    case Step.UNCONFIRMED:
      return StepEnum.UNCONFIRMED;
    case Step.ADDITIONALINFORMATION:
      return StepEnum.ADDITIONAL_INFORMATION;
    case Step.CANCELED:
      return StepEnum.CANCELED;
  }

  return assertUnreachable(x);
};

export const convertPlatformUrl = (x: PlatformUrl) => {
  switch (x) {
    case PlatformUrl.TERMS_AND_CONDITIONS:
      return 'tosUrl';
    case PlatformUrl.PRIVACY_POLICY:
      return 'privacyUrl';
    case PlatformUrl.IMPRINT:
      return 'imprintUrl';
  }

  return assertUnreachable(x);
};
